import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { saveAs } from 'file-saver';
import { t } from '@lingui/macro';
import { IconButton, Tooltip } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import makeStyles from '@material-ui/core/styles/makeStyles';

import buildCsv, { FILE_TYPE_STRING, buildCsvFileName, EXPORT_TOO_LARGE } from './CSVBuilder';
import useUiAlerts, { createAlertConfig, ERROR, WARNING } from '../../stores/AlertsStore';
import useIsPrinting from '../../utilities/PrintUtilities';

const useStyles = makeStyles((theme) => ({
    csvContainer: {
        marginLeft: '10px',
        '& svg': {
            ...theme.mixins.flex.displayFlex,
            ...theme.mixins.flex.alignItemsCenter,
        },
        '& button': {
            padding: 0,
            '&:hover': {
                background: 'none',
            },
        },
    },
}));

const toastMessage = t`Your file is being generated and will download when it is ready.`;
const toastError = t`Something went wrong while your file was being generated. Please try again later.`;

export default function CsvExport({ columns, loader, sorters, filters, params }) {
    const classes = useStyles();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [disableExport, setDisableExport] = useState(false);
    const [, uiAlertActions] = useUiAlerts();
    const isPrinting = useIsPrinting();

    function handleExport() {
        let snackBarKey;
        const csvFileName = buildCsvFileName();
        return Promise.resolve()
            .then(() => {
                setDisableExport(true);
                snackBarKey = enqueueSnackbar(toastMessage, { persist: true });
            })
            .then(() => buildCsv(columns, loader, sorters, filters, params))
            .then((response) =>
                saveAs(new Blob([response], { type: FILE_TYPE_STRING }), csvFileName, {
                    autoBom: true,
                })
            )
            .then(() => {
                setDisableExport(false);
                setTimeout(() => closeSnackbar(snackBarKey), 500);
            })
            .catch((err) => {
                setDisableExport(false);
                closeSnackbar(snackBarKey);
                if (err.code === EXPORT_TOO_LARGE) {
                    uiAlertActions.createAlert(createAlertConfig(WARNING, err.message));
                    return;
                }
                uiAlertActions.createAlert(createAlertConfig(ERROR, toastError));
            });
    }

    if (!loader) {
        return null;
    }

    if (isPrinting) {
        return null;
    }

    return (
        <div className={classes.csvContainer}>
            <Tooltip title={t`Export data to a file`} placement="bottom">
                <IconButton onClick={handleExport} disabled={disableExport} disableFocusRipple disableRipple>
                    <GetAppIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </div>
    );
}
